<script setup lang="ts">

import GenericIconWithColoredNumber from '@/components/Elements/Images/Icons/GenericIconWithColoredNumber.vue'
import { useShipDesignCreationStore } from '@/stores/shipDesignCreationStore.ts'
import { computed } from 'vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { useAssetStore } from '@/stores/assetStore.ts'
import SimpleProgressBar from '@/components/Elements/ProgressBar/SimpleProgressBar.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Stores / models
 */
const shipDesignCreationStore = useShipDesignCreationStore()
const gameStore = useGameStore()
const assetStore = useAssetStore()

/**
 * Function
 */
const save = () => {
  gameStore.modalType = 'createShipDesignSave'
}

const importDesign = () => {
  // Create a hidden file input
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.ferion'

  input.onchange = (e: Event) => {
    const target = e.target as HTMLInputElement
    const file = target.files?.[0]

    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string
        const importData = JSON.parse(content)

        // Validate the data structure
        if (!importData.componentCodes || !Array.isArray(importData.componentCodes)) {
          gameStore.setTinyErrorToastMessage(t('command.shipDesigns.import.invalidFormat'))
          return
        }

        // Verify player has access to all components
        const unavailableComponents: string[] = []
        const availableComponents = []

        for (const componentCode of importData.componentCodes) {
          const component = assetStore.findShipComponentByCode(componentCode)

          if (!component) {
            unavailableComponents.push(componentCode)
            continue
          }

          // Check if player has access to this component
          const hasAccess = Array.from(gameStore.playerShipComponents.values()).includes(componentCode)

          if (!hasAccess) {
            unavailableComponents.push(componentCode)
          } else {
            availableComponents.push(component)
          }
        }

        // If any components are unavailable, show error
        if (unavailableComponents.length > 0) {
          gameStore.setTinyErrorToastMessage(t('command.shipDesigns.import.missingComponents', { count: unavailableComponents.length }))
          return
        }

        // All components are available - clear current design and import
        shipDesignCreationStore.resetDesign()

        // Set the imported design name
        const designName = importData.name || ''
        shipDesignCreationStore.designName = designName

        // Add all components
        for (const component of availableComponents) {
          shipDesignCreationStore.addComponent(component, false)
        }

        // Recalculate the design stats
        shipDesignCreationStore.recalculateDesign()

        // Success message
        if (designName) {
          gameStore.setTinySuccessToastMessage(t('command.shipDesigns.import.successWithName', { count: availableComponents.length, name: designName }))
        } else {
          gameStore.setTinySuccessToastMessage(t('command.shipDesigns.import.success', { count: availableComponents.length }))
        }

      } catch (error) {
        gameStore.setTinyErrorToastMessage(t('command.shipDesigns.import.failed'))
      }
    }

    reader.readAsText(file)
  }

  input.click()
}

/**
 * Computed
 */
//Calculate the percentage of the ship size in use (make reactive)
const shipSizePercentage = computed(() => {
  if(shipDesignCreationStore.shipFrame === 0) return 0
  const percentage = (shipDesignCreationStore.shipMass / shipDesignCreationStore.shipFrame) * 100
  if(percentage > 100) return 100
  return percentage
})

const overloaded = computed(() => {
  return shipDesignCreationStore.shipFrame < shipDesignCreationStore.shipMass
})

</script>

<template>
  <div class="">
    <div class="flex pb-1">
      <div class="flex-shrink-0 rounded bg-metal-800 p-2 mr-1 flex items-center justify-center">
        <ActionButton @click="importDesign" :for-icon="true">
          <GenericIcon type="import" :tooltip="t('modals.createShipDesign.tooltips.importDesign')"/>
        </ActionButton>
      </div>
      <div class="p-2 rounded bg-metal-800 flex justify-center flex-grow">
        <div class="flex-shrink-0 px-2 self-center">
          <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipMass" type="mass" :shortFormat="false"/>
        </div>
        <div class="flex-grow px-1 self-center">
          <SimpleProgressBar
            :percentage="shipSizePercentage"
            :color="overloaded ? 'red' : 'green'"
            :height="5"
          />
        </div>
        <div class="flex-shrink-0 px-2 py-1 self-center">
          <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipFrame" type="frame" :shortFormat="false"/>
        </div>
      </div>
      <div class="flex-shrink-0 rounded bg-metal-800 p-2 ml-1" v-if="shipDesignCreationStore.hasEngine && !overloaded">
        <ActionButton @click="save">{{ t('common.actions.save') }}</ActionButton>
      </div>
    </div>
    <div class="grid grid-cols-5 gap-1">
      <div class="p-2 rounded stat-card flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipAttackShips" type="attack_against_ships"/>
      </div>
      <div class="p-2 rounded stat-card flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipAttackPlanets" type="attack_against_planets"/>
      </div>
      <div class="p-2 rounded stat-card flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipShields" type="shields"/>
      </div>
      <div class="p-2 rounded stat-card flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipHitPoints" type="hit_points"/>
      </div>
      <div class="p-2 rounded stat-card flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipMaxSpeed" type="max_speed"/>
      </div>
      <div class="p-2 rounded stat-card flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipScanningRange" type="scanning_range"/>
      </div>
      <div class="p-2 rounded stat-card flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipRepair" type="repair"/>
      </div>
      <div class="p-2 rounded stat-card flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.shipProductionCost" type="production"/>
      </div>
      <div class="p-2 rounded stat-card flex justify-center">
        <GenericIconWithColoredNumber :amount="shipDesignCreationStore.maintenanceCost" type="credit"/>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.stat-card {
  background: linear-gradient(to bottom, theme('colors.metal.800') 0%, theme('colors.metal.900') 100%);
}
</style>